/**
 * Direction arrows on the spatial-view players — the geometry, kept pure.
 *
 * Direction is measured, not guessed: the player's position now against
 * their position a short LOOKBACK ago, from the same tracking cache the
 * dot itself is drawn from. Tracking x/y are CENTIMETRES from the centre
 * spot (KNOWLEDGE.md; convertToCanvasCoords says the same), so speed
 * falls out in m/s and the walk/sprint thresholds mean something.
 *
 * A player slower than a brisk walk gets NO arrow: at standstill the
 * frame-to-frame delta is tracking noise, and 22 jittering arrows would
 * cost more legibility than 15 true ones buy.
 */

/** How far back to measure. Short enough to follow a cut, long enough
 *  that tracking noise cancels. */
export const LOOKBACK_SECONDS = 0.4
/** Below this the arrow is noise, not intent. */
export const MIN_SPEED_MS = 1.0
/** At and beyond a sprint the arrow stops growing. */
export const MAX_SPEED_MS = 8.0

export type Vec = { x: number; y: number }

/** Unit direction (in tracking cm-space) and speed, or null when the
 *  player has no previous fix or is below the walk threshold. */
export function arrowVector(
  prev: Vec | null | undefined,
  curr: Vec,
  dtSeconds: number,
): { ux: number; uy: number; speedMs: number } | null {
  if (!prev || dtSeconds <= 0) return null
  const dx = curr.x - prev.x
  const dy = curr.y - prev.y
  const dist = Math.hypot(dx, dy)
  if (dist === 0) return null
  const speedMs = dist / 100 / dtSeconds
  if (!Number.isFinite(speedMs) || speedMs < MIN_SPEED_MS) return null
  return { ux: dx / dist, uy: dy / dist, speedMs }
}

/** Arrow length in canvas px — walk to sprint spans min..max, clamped. */
export function arrowLength(
  speedMs: number, minPx = 7, maxPx = 16,
): number {
  const t = Math.min(Math.max(
    (speedMs - MIN_SPEED_MS) / (MAX_SPEED_MS - MIN_SPEED_MS), 0), 1)
  return minPx + t * (maxPx - minPx)
}

/** The three strokes of an arrow leaving a dot of radius r at canvas
 *  `pos`, pointing along the CANVAS-space unit direction (the caller
 *  negates uy: pitch y is up-positive, canvas y down-positive). */
export function arrowStrokes(
  pos: Vec, unit: Vec, r: number, len: number,
): { shaft: [Vec, Vec]; head: [[Vec, Vec], [Vec, Vec]] } {
  const from = { x: pos.x + unit.x * (r + 2), y: pos.y + unit.y * (r + 2) }
  const to = { x: from.x + unit.x * len, y: from.y + unit.y * len }
  // Head barbs: back from the tip, rotated ±150° off the direction.
  const barb = (sign: 1 | -1): [Vec, Vec] => {
    const a = Math.atan2(unit.y, unit.x) + sign * (Math.PI * 5 / 6)
    return [to, { x: to.x + Math.cos(a) * 5, y: to.y + Math.sin(a) * 5 }]
  }
  return { shaft: [from, to], head: [barb(1), barb(-1)] }
}
